<template>
	<div class="tour">
		<template v-if="tour.active.value">
			<!--
				Il ritaglio toglie la parte scura sopra l'elemento evidenziato e con essa
				anche l'area sensibile al mouse: fuori dal foro l'overlay intercetta i
				click, dentro il foro la pagina resta viva.
			-->
			<div class="tour__dim" :style="{ clipPath: cutout }"></div>

			<div v-if="tour.rect.value" class="tour__ring" :style="ringStyle"></div>

			<div ref="cardEl" class="tour__card" :class="{ 'tour__card--busy': tour.busy.value }" :style="cardStyle" @keydown="onKeydown">
				<div v-if="placed.arrow !== 'none'" class="tour__arrow" :class="`tour__arrow--${placed.arrow}`"></div>

				<v-card :elevation="8" role="dialog" aria-modal="true" tabindex="-1" :aria-labelledby="titleId" :aria-describedby="bodyId">
					<v-card-title :id="titleId" class="title tour__title">{{ stepTitle }}</v-card-title>

					<v-card-text :id="bodyId" class="body-2">{{ stepBody }}</v-card-text>

					<v-card-actions class="tour__actions">
						<v-btn small text @click="tour.stop()">Salta</v-btn>

						<span aria-live="polite" class="caption text--secondary">Passo {{ tour.index.value + 1 }} di {{ tour.total.value }}</span>

						<v-spacer></v-spacer>

						<v-btn v-if="tour.index.value > 0" small text :disabled="tour.busy.value" @click="tour.prev()">Indietro</v-btn>
						<v-btn small depressed color="primary" :disabled="tour.busy.value" @click="tour.next()">{{ isLast ? 'Fine' : 'Avanti' }}</v-btn>
					</v-card-actions>
				</v-card>
			</div>
		</template>
	</div>
</template>

<script>
/** Distanza fra il bordo dell'elemento evidenziato e la scheda. */
const GAP = 12;
/** Margine minimo fra la scheda e i bordi della finestra. */
const MARGIN = 8;
/** Larghezza della scheda: oltre questa il testo diventa faticoso da leggere. */
const CARD_WIDTH = 340;
/** Quanto la punta della freccia resta lontana dagli angoli della scheda. */
const ARROW_INSET = 20;

function clamp(value, min, max) {
	return Math.min(Math.max(value, min), max);
}

/** Punta allineata al centro del bersaglio, ma mai a ridosso degli angoli. */
function arrowAt(center, start, length) {
	return clamp(center - start, ARROW_INSET, Math.max(ARROW_INSET, length - ARROW_INSET));
}

/** L'alone intorno all'anello riprende il colore del tema, smorzato. */
function fade(hex, alpha) {
	const match = /^#?([\da-f]{6})$/i.exec(String(hex || ''));

	if (!match) {
		return `rgba(1, 87, 155, ${alpha})`;
	}

	const value = parseInt(match[1], 16);

	return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
}

export default {
	name: 'TourOverlay',
	props: {
		tour: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			cardSize: { height: 200, width: CARD_WIDTH },
			/*
			 * `place()` legge le dimensioni della finestra, che non sono reattive.
			 * Senza questo contatore la scheda agganciata - quella dei passi senza
			 * bersaglio - resterebbe alle vecchie coordinate dopo un ridimensionamento.
			 */
			viewportTick: 0,
			observer: null,
			restoreFocus: null,
		};
	},
	computed: {
		stepTitle() {
			const step = this.tour.step.value;

			return step ? step.title : '';
		},
		stepBody() {
			const step = this.tour.step.value;

			return step ? step.body : '';
		},
		stepKey() {
			const step = this.tour.step.value;

			return step ? step.key : 'none';
		},
		titleId() {
			return `tour-title-${this.stepKey}`;
		},
		bodyId() {
			return `tour-body-${this.stepKey}`;
		},
		isLast() {
			return this.tour.index.value === this.tour.total.value - 1;
		},
		primary() {
			return this.$vuetify.theme.currentTheme.primary;
		},
		cutout() {
			const spot = this.tour.rect.value;

			if (!spot) {
				return 'none';
			}

			const left = Math.round(spot.left);
			const top = Math.round(spot.top);
			const right = Math.round(spot.left + spot.width);
			const bottom = Math.round(spot.top + spot.height);

			// Poligono "a fessura": entra dal bordo sinistro, gira intorno al foro e
			// torna indietro. Vale con la regola di riempimento predefinita, e avendo
			// sempre lo stesso numero di vertici si puo' animare.
			return `polygon(0 0, 0 100%, ${left}px 100%, ${left}px ${top}px, ${right}px ${top}px, ${right}px ${bottom}px, ${left}px ${bottom}px, ${left}px 100%, 100% 100%, 100% 0)`;
		},
		ringStyle() {
			const spot = this.tour.rect.value;

			if (!spot) {
				return undefined;
			}

			return {
				boxShadow: `0 0 0 4px ${fade(this.primary, 0.28)}`,
				borderColor: this.primary,
				height: `${spot.height}px`,
				left: `${spot.left}px`,
				top: `${spot.top}px`,
				width: `${spot.width}px`,
			};
		},
		placed() {
			const step = this.tour.step.value;

			return this.place(this.tour.rect.value, this.cardSize, step && step.placement ? step.placement : 'auto');
		},
		cardStyle() {
			return {
				left: `${this.placed.left}px`,
				top: `${this.placed.top}px`,
				'--tour-arrow-offset': `${this.placed.arrowOffset}px`,
			};
		},
	},
	watch: {
		'tour.active.value': {
			async handler(isActive) {
				if (isActive) {
					this.restoreFocus = document.activeElement;
					document.addEventListener('keydown', this.onDocumentKeydown);
					window.addEventListener('resize', this.onViewportResize, { passive: true });
					await this.nextFrame();
					this.measureCard();

					if (this.$refs.cardEl) {
						this.observer = new ResizeObserver(this.measureCard);
						this.observer.observe(this.$refs.cardEl);

						const card = this.$refs.cardEl.querySelector('.v-card');

						if (card) {
							card.focus();
						}
					}

					return;
				}

				this.teardown();
			},
		},
	},
	mounted() {
		/*
		 * Vue 2 non ha `<Teleport>`. L'overlay deve stare fuori dalla `v-dialog` che
		 * lo ospita, altrimenti il velo scuro coprirebbe solo il contenuto della
		 * dialog invece della finestra intera.
		 *
		 * Mai `document.body`: **tutto il CSS di Vuetify 2 e' annidato sotto
		 * `.v-application`** - `.v-application .primary`, `.v-application .body-2`,
		 * `.v-application .elevation-8` - e fuori di li' la card perderebbe colore,
		 * font ed elevazione.
		 *
		 * Quando la pagina vive dentro una dialog, la destinazione e' il
		 * `.v-dialog__content` che la contiene, non `[data-app]`. Non e' una
		 * preferenza: `closeConditional` di `VDialog` chiude la finestra a ogni click
		 * che non sia dentro `$refs.content`, e la verifica passa per `click-outside`,
		 * registrato su `document` **in cattura** - quindi nessuno `stop` a valle
		 * arriverebbe in tempo, e fermare l'evento a monte spegnerebbe anche i
		 * pulsanti del tutorial. Stando dentro quel nodo i click risultano interni e
		 * la dialog resta aperta. Fuori da una dialog si ricade su `[data-app]`, lo
		 * stesso posto dove Vuetify stacca le proprie finestre.
		 *
		 * Vue rimuove il nodo leggendo il suo `parentNode` effettivo, quindi lo
		 * spostamento non rompe lo smontaggio.
		 */
		const target = this.$el.closest('.v-dialog__content') || document.querySelector('[data-app]') || document.body;

		// Idempotente: in sviluppo l'HMR puo' rieseguire il montaggio.
		if (this.$el.parentNode !== target) {
			target.appendChild(this.$el);
		}
	},
	beforeDestroy() {
		this.teardown();

		if (this.$el.parentNode) {
			this.$el.parentNode.removeChild(this.$el);
		}
	},
	methods: {
		teardown() {
			document.removeEventListener('keydown', this.onDocumentKeydown);
			window.removeEventListener('resize', this.onViewportResize);

			if (this.observer) {
				this.observer.disconnect();
				this.observer = null;
			}

			if (this.restoreFocus && this.restoreFocus.focus) {
				this.restoreFocus.focus();
			}

			this.restoreFocus = null;
		},
		nextFrame() {
			return new Promise(done => requestAnimationFrame(() => done()));
		},
		onViewportResize() {
			this.viewportTick += 1;
		},
		/**
		 * Sceglie il lato su cui appoggiare la scheda e la tiene dentro la finestra.
		 * E' il minimo indispensabile - prova i lati in ordine, poi allinea - perche'
		 * in progetto non c'e' una libreria di posizionamento.
		 */
		place(spot, size, preferred) {
			// Letto per farsi ricalcolare dopo un ridimensionamento della finestra.
			void this.viewportTick;

			const viewWidth = window.innerWidth;
			const viewHeight = window.innerHeight;
			const maxLeft = Math.max(MARGIN, viewWidth - size.width - MARGIN);
			const maxTop = Math.max(MARGIN, viewHeight - size.height - MARGIN);

			// Senza bersaglio la scheda si comporta come un pannello: in basso al centro.
			const docked = {
				arrow: 'none',
				arrowOffset: 0,
				left: clamp((viewWidth - size.width) / 2, MARGIN, maxLeft),
				top: maxTop,
			};

			if (!spot) {
				return docked;
			}

			const spotRight = spot.left + spot.width;
			const spotBottom = spot.top + spot.height;
			const centerX = spot.left + spot.width / 2;
			const centerY = spot.top + spot.height / 2;
			const sides = ['bottom', 'top', 'right', 'left'];
			const order = preferred === 'auto' ? sides : [preferred, ...sides.filter(side => side !== preferred)];

			for (const side of order) {
				if (side === 'bottom' && spotBottom + GAP + size.height <= viewHeight - MARGIN) {
					const left = clamp(centerX - size.width / 2, MARGIN, maxLeft);

					return { arrow: 'top', arrowOffset: arrowAt(centerX, left, size.width), left, top: spotBottom + GAP };
				}

				if (side === 'top' && spot.top - GAP - size.height >= MARGIN) {
					const left = clamp(centerX - size.width / 2, MARGIN, maxLeft);

					return { arrow: 'bottom', arrowOffset: arrowAt(centerX, left, size.width), left, top: spot.top - GAP - size.height };
				}

				if (side === 'right' && spotRight + GAP + size.width <= viewWidth - MARGIN) {
					const top = clamp(centerY - size.height / 2, MARGIN, maxTop);

					return { arrow: 'left', arrowOffset: arrowAt(centerY, top, size.height), left: spotRight + GAP, top };
				}

				if (side === 'left' && spot.left - GAP - size.width >= MARGIN) {
					const top = clamp(centerY - size.height / 2, MARGIN, maxTop);

					return { arrow: 'right', arrowOffset: arrowAt(centerY, top, size.height), left: spot.left - GAP - size.width, top };
				}
			}

			return docked;
		},
		focusable() {
			if (!this.$refs.cardEl) {
				return [];
			}

			return Array.from(this.$refs.cardEl.querySelectorAll('button:not([disabled])'));
		},
		/**
		 * La parte scura ferma il mouse ma non il tabulatore: senza questo il fuoco
		 * uscirebbe dalla scheda e finirebbe su comandi che ora non sono raggiungibili.
		 */
		onKeydown(nativeEvent) {
			if (nativeEvent.key !== 'Tab') {
				return;
			}

			const items = this.focusable();

			if (!items.length) {
				return;
			}

			const first = items[0];
			const last = items[items.length - 1];
			const current = document.activeElement;

			if (nativeEvent.shiftKey && (current === first || current === this.$refs.cardEl)) {
				nativeEvent.preventDefault();
				last.focus();
			} else if (!nativeEvent.shiftKey && current === last) {
				nativeEvent.preventDefault();
				first.focus();
			}
		},
		onDocumentKeydown(nativeEvent) {
			if (!this.tour.active.value) {
				return;
			}

			if (nativeEvent.key === 'Escape') {
				nativeEvent.preventDefault();
				this.tour.stop();

				return;
			}

			if (this.tour.busy.value) {
				return;
			}

			if (nativeEvent.key === 'ArrowRight') {
				nativeEvent.preventDefault();
				this.tour.next();
			} else if (nativeEvent.key === 'ArrowLeft') {
				nativeEvent.preventDefault();
				this.tour.prev();
			}
		},
		measureCard() {
			if (!this.$refs.cardEl) {
				return;
			}

			const box = this.$refs.cardEl.getBoundingClientRect();

			if (box.width || box.height) {
				this.cardSize = { height: box.height, width: box.width };
			}
		},
	},
};
</script>

<style scoped>
/*
 * La radice sta sempre montata (Vue 2 non regge un `v-if` sulla radice di un
 * elemento spostato a mano) e non deve pesare sul layout: `.v-application` e'
 * un contenitore flex, quindi un div in flusso normale diventerebbe un suo
 * elemento. Fissa e di area nulla non lo e'; i figli si posizionano da soli
 * rispetto alla finestra.
 */
.tour {
	height: 0;
	left: 0;
	position: fixed;
	top: 0;
	width: 0;
	/*
	 * Lo z-index va sulla radice, non solo sui figli: `position: fixed` apre gia'
	 * un contesto di impilamento, quindi i 2500 qui sotto si giocherebbero fra
	 * loro e l'intero overlay resterebbe all'altezza `auto` - cioe' sotto la
	 * `v-dialog`, che Vuetify porta a 202. Da fuori il tutorial sembrava non
	 * partire affatto.
	 */
	z-index: 2500;
}

/*
 * `pointer-events: auto` esplicito: dentro una dialog il contenitore
 * `.v-dialog__content` li disattiva per i propri figli, e senza questo il velo
 * lascerebbe passare i click alla pagina che sta oscurando.
 */
.tour__dim {
	background: rgb(0 0 0 / 55%);
	inset: 0;
	pointer-events: auto;
	position: fixed;
	transition: clip-path 0.22s ease;
	z-index: 2500;
}

.tour__ring {
	border: 2px solid;
	border-radius: 6px;
	pointer-events: none;
	position: fixed;
	transition: all 0.22s ease;
	z-index: 2501;
}

.tour__card {
	max-width: min(340px, calc(100vw - 24px));
	pointer-events: auto;
	position: fixed;
	transition: opacity 0.15s ease;
	width: 340px;
	z-index: 2502;
}

/* Mentre la pagina si prepara la scheda si spegne invece di saltare altrove. */
.tour__card--busy {
	opacity: 0.6;
	pointer-events: none;
}

/* Il titolo di Vuetify 2 non va a capo da solo: i titoli lunghi uscirebbero. */
.tour__title {
	word-break: break-word;
}

.tour__actions {
	gap: 8px;
}

/* Quadrato ruotato dietro la scheda: ne spunta solo la meta' esterna. */
.tour__arrow {
	background: var(--v-surface-base, #fff);
	height: 12px;
	position: absolute;
	transform: rotate(45deg);
	width: 12px;
}

.tour__arrow--top {
	left: calc(var(--tour-arrow-offset) - 6px);
	top: -5px;
}

.tour__arrow--bottom {
	bottom: -5px;
	left: calc(var(--tour-arrow-offset) - 6px);
}

.tour__arrow--left {
	left: -5px;
	top: calc(var(--tour-arrow-offset) - 6px);
}

.tour__arrow--right {
	right: -5px;
	top: calc(var(--tour-arrow-offset) - 6px);
}

@media (prefers-reduced-motion: reduce) {
	.tour__card,
	.tour__dim,
	.tour__ring {
		transition: none;
	}
}
</style>
